// Password validation utilities

export interface PasswordRequirements {
	minLength: boolean;
	hasUppercase: boolean;
	hasLowercase: boolean;
	hasNumber: boolean;
	hasSpecialChar: boolean;
}

// Progressive validation interface for UI components
export interface PasswordRequirement {
	id: string;
	label: string;
	test: (password: string) => boolean;
	met: boolean;
}

/**
 * Check password against security requirements
 * @param password Password to validate
 * @returns Object with requirement validation results
 */
export function checkPasswordRequirements(
	password: string,
): PasswordRequirements {
	return {
		minLength: password.length >= 8,
		hasUppercase: /[A-Z]/.test(password),
		hasLowercase: /[a-z]/.test(password),
		hasNumber: /\d/.test(password),
		hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
	};
}

/**
 * Calculate password strength score
 * @param password Password to evaluate
 * @returns Strength score from 0-100
 */
export function calculatePasswordStrength(password: string): number {
	const requirements = checkPasswordRequirements(password);
	const score = Object.values(requirements).filter(Boolean).length;

	// Additional points for length
	let lengthBonus = 0;
	if (password.length >= 12) lengthBonus = 20;
	else if (password.length >= 10) lengthBonus = 10;

	return Math.min(100, score * 16 + lengthBonus);
}

/**
 * Check if password is strong enough
 * @param password Password to validate
 * @returns True if password meets minimum requirements
 */
export function isPasswordStrong(password: string): boolean {
	const requirements = checkPasswordRequirements(password);
	return (
		requirements.minLength &&
		requirements.hasUppercase &&
		requirements.hasLowercase &&
		(requirements.hasNumber || requirements.hasSpecialChar)
	);
}

/**
 * Get password requirements with progressive validation for UI
 * Compatible with existing web and mobile implementations
 * @param password Password to validate
 * @returns Array of requirement objects with validation state
 */
export function getPasswordRequirementsArray(
	password: string,
): PasswordRequirement[] {
	return [
		{
			id: "length",
			label: "8+ characters",
			test: (pwd) => pwd.length >= 8,
			met: password.length >= 8,
		},
		{
			id: "uppercase",
			label: "1 uppercase",
			test: (pwd) => /[A-Z]/.test(pwd),
			met: /[A-Z]/.test(password),
		},
		{
			id: "lowercase",
			label: "1 lowercase",
			test: (pwd) => /[a-z]/.test(pwd),
			met: /[a-z]/.test(password),
		},
		{
			id: "number",
			label: "1 number",
			test: (pwd) => /\d/.test(pwd),
			met: /\d/.test(password),
		},
		{
			id: "special",
			label: "1 symbol",
			test: (pwd) => /[!@#$%^&*()_+\-={};"'|,.<>/?]/.test(pwd),
			met: /[!@#$%^&*()_+\-={};"'|,.<>/?]/.test(password),
		},
	];
}
